import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Key, ArrowRight, Check, Clock, Shield, Star, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

// Images
const HERO_IMAGE = "https://images.unsplash.com/photo-1735547876935-7be80eae1c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxhcGFydG1lbnQlMjBtb3ZpbmclMjBvdXQlMjBjbGVhbmluZyUyMGtleXN8ZW58MHx8fHwxNzc0MTE4NzUzfDA&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_1 = "https://images.pexels.com/photos/7489107/pexels-photo-7489107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_2 = "https://images.pexels.com/photos/1571467/pexels-photo-1571467.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const NettoyageApresBailPage = () => {
  const questions = [
    { id: 'nb_pieces', label: 'Nombre de pièces ?', type: 'select', options: ['Studio', '2 pièces', '3 pièces', '4 pièces', '5+ pièces'] },
    { id: 'date_remise', label: 'Date de remise des clés ?', type: 'select', options: ['Cette semaine', 'Semaine prochaine', 'Dans 2 semaines', 'Plus tard'] },
    { id: 'etat', label: 'État actuel du logement ?', type: 'select', options: ['Correct', 'À nettoyer en profondeur', 'Très sale', 'Je ne sais pas'] },
    { id: 'balcon', label: 'Balcon ou terrasse à nettoyer ?', type: 'select', options: ['Oui', 'Non'] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* ============================================
          PILIER 1: ATTENTION - Hero accrocheur
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] flex items-center">
        <img src={HERO_IMAGE} alt="Remise des clés appartement" className="hero-image-bg" />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Key className="h-10 w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-sm">
                Homely Nettoyage
              </Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              Récupérez 100% de votre dépôt de garantie
            </h1>
            
            <p className="text-lg sm:text-xl text-white/90 mb-6 max-w-2xl">
              Nettoyage après bail professionnel en Suisse romande. 
              État des lieux impeccable garanti ou nous repassons gratuitement.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Shield className="h-5 w-5 text-green-400" />
                <span className="text-white text-sm">Garantie satisfaction</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Clock className="h-5 w-5 text-yellow-400" />
                <span className="text-white text-sm">Intervention sous 48h</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Star className="h-5 w-5 text-yellow-400" />
                <span className="text-white text-sm">4.9/5 (127 avis)</span>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="btn-premium h-14 px-10 text-base rounded-full"
              onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
            >
              Obtenir un devis en 2 min
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 2: ÉMOTION - Le problème (douleur)
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-6 w-6 text-orange-500" />
                <span className="text-orange-500 font-semibold">Le saviez-vous ?</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                67% des locataires perdent une partie de leur dépôt à cause du nettoyage
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                La remise des clés approche et le stress monte. Vous avez passé des heures à nettoyer, 
                mais êtes-vous certain que ce sera suffisant pour la régie ?
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
                  <span className="text-red-500 font-bold">✗</span>
                  <div>
                    <p className="font-semibold text-red-800">Traces de calcaire dans la salle de bain</p>
                    <p className="text-sm text-red-600">Retenue moyenne: 150-300 CHF</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
                  <span className="text-red-500 font-bold">✗</span>
                  <div>
                    <p className="font-semibold text-red-800">Four et hotte non dégraissés</p>
                    <p className="text-sm text-red-600">Retenue moyenne: 200-400 CHF</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
                  <span className="text-red-500 font-bold">✗</span>
                  <div>
                    <p className="font-semibold text-red-800">Sols et plinthes négligés</p>
                    <p className="text-sm text-red-600">Retenue moyenne: 100-250 CHF</p>
                  </div>
                </div>
              </div>

              <p className="text-lg font-semibold" style={{ color: 'var(--sage-dark)' }}>
                Ne prenez pas ce risque. Confiez votre nettoyage à des professionnels.
              </p>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Remise des clés nouveau logement" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <p className="font-bold text-2xl" style={{ color: 'var(--sage)' }}>1'500+ CHF</p>
                <p className="text-sm text-muted-foreground">Montant moyen d'un dépôt de garantie en Suisse</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 3: MÉMOIRE - La solution (bénéfices)
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Notre solution</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Un nettoyage après bail qui ne laisse rien au hasard
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={CONTENT_IMAGE_2} 
                alt="Appartement impeccable après nettoyage" 
                className="rounded-2xl shadow-xl w-full"
              />
            </div>

            <div className="order-1 lg:order-2">
              <h3 className="text-2xl font-bold mb-6">Notre checklist complète inclut :</h3>
              
              <div className="space-y-4">
                {[
                  { title: 'Cuisine', items: ['Four, hotte et plaques dégraissés', 'Frigo nettoyé intérieur/extérieur', 'Placards vidés et essuyés'] },
                  { title: 'Salle de bain', items: ['Détartrage complet', 'Joints nettoyés', 'Miroirs et robinetterie brillants'] },
                  { title: 'Pièces de vie', items: ['Sols lavés et cirés si nécessaire', 'Plinthes et interrupteurs', 'Vitres intérieures'] },
                  { title: 'Finitions', items: ['Balcon/terrasse', 'Stores et volets', 'Caves et dépendances'] }
                ].map((section, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl shadow-sm">
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--sage-dark)' }}>{section.title}</h4>
                    <ul className="space-y-1">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 4: DÉCISION - Preuves sociales
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils nous ont fait confiance
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { name: 'Marie L.', city: 'Lausanne', text: 'État des lieux validé du premier coup ! J\'ai récupéré l\'intégralité de mon dépôt. Merci Homely !', rating: 5 },
              { name: 'Pierre D.', city: 'Genève', text: 'Équipe ponctuelle et professionnelle. Mon appartement n\'avait jamais été aussi propre en 5 ans de location.', rating: 5 },
              { name: 'Sophie M.', city: 'Montreux', text: 'Intervention rapide 3 jours avant la remise des clés. Résultat impeccable, je recommande à 100%.', rating: 5 }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold">{review.name}</p>
                    <p className="text-sm text-muted-foreground">{review.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '98%', label: 'Dépôts récupérés' },
              { value: '1\'200+', label: 'Appartements nettoyés' },
              { value: '48h', label: 'Délai d\'intervention' },
              { value: '4.9/5', label: 'Note moyenne' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-3xl md:text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 5: ACTION - CTA puissant
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Protégez votre dépôt de garantie
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Ne laissez pas le stress de la remise des clés gâcher votre déménagement. 
                Obtenez votre devis gratuit en moins de 2 minutes.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Devis gratuit et sans engagement',
                  'Intervention possible sous 48h',
                  'Garantie satisfaction ou repasse gratuite',
                  'Facture acceptée par toutes les régies'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>

              {/* Urgency */}
              <div className="p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 font-semibold">
                  Période chargée : réservez maintenant pour garantir votre créneau !
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <ServiceLeadForm service="nettoyage" pole="nettoyage" questions={questions} variant="ultra" />
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h3 className="font-semibold mb-4">Autres services de nettoyage</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/nettoyage/canape" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage canapé</Link>
            <Link to="/nettoyage/terrasse" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage terrasse</Link>
            <Link to="/nettoyage/vitres" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage vitres</Link>
            <Link to="/nettoyage/fin-de-chantier" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Fin de chantier</Link>
            <Link to="/nettoyage" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Tous nos services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
